import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Sparkles,
  Copy,
  Check,
  ExternalLink,
  Ban,
  RefreshCw,
} from "lucide-react";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { Textarea } from "../../ui/textarea.js";

/**
 * Admin operator surface for the prospect Team compiler (YAG-237).
 *
 * Compile form -> POST /api/admin/prospects/compile; artifacts table with a
 * Recent / Most engaged sort toggle mapping to the list route's `sort` param;
 * per-row copy public link, preview (no-stamp authed route), and revoke
 * (confirm — the share page is external-facing). Backend contract:
 * `routes/adminProspects.ts`.
 */

type ProspectSourceKind = "job_posting" | "notes";
type ProspectStatus = "draft" | "ready" | "sent" | "revoked";
type ProspectSort = "recent" | "engaged";

/** Mirrors the `toListItem` projection returned by GET /api/admin/prospects. */
interface ProspectArtifactListItem {
  id: string;
  token: string;
  companyDomain: string;
  companyName: string | null;
  roleTitle: string;
  sourceKind: ProspectSourceKind;
  status: ProspectStatus;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  claimedAt: string | null;
  claimantEmail: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function statusColor(status: ProspectStatus): string {
  if (status === "ready") return "var(--success)";
  if (status === "sent") return "var(--info)";
  if (status === "revoked") return "var(--error)";
  return "var(--text-tertiary)";
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString();
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const ms = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function shareLink(token: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/share/team/${token}`;
}

export function ProspectArtifactsPanel() {
  const [artifacts, setArtifacts] = useState<ProspectArtifactListItem[]>([]);
  const [sort, setSort] = useState<ProspectSort>("recent");
  const [loading, setLoading] = useState(true);

  // Compile form.
  const [sourceText, setSourceText] = useState("");
  const [domain, setDomain] = useState("");
  const [roleHint, setRoleHint] = useState("");
  const [sourceKind, setSourceKind] = useState<ProspectSourceKind>("job_posting");
  const [compiling, setCompiling] = useState(false);
  const [compileError, setCompileError] = useState<string | null>(null);

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/prospects?sort=${sort}`);
      if (!res.ok) return;
      const body = (await res.json()) as {
        sort: ProspectSort;
        artifacts: ProspectArtifactListItem[];
      };
      setArtifacts(body.artifacts);
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCompile() {
    const text = sourceText.trim();
    const dom = domain.trim();
    if (!text || !dom) {
      setCompileError("Source text and company domain are required");
      return;
    }
    setCompiling(true);
    setCompileError(null);
    try {
      const res = await fetch("/api/admin/prospects/compile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceText: text,
          domain: dom,
          sourceKind,
          roleHint: roleHint.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setCompileError(body.error ?? `Compile failed (${res.status})`);
        return;
      }
      setSourceText("");
      setDomain("");
      setRoleHint("");
      setSourceKind("job_posting");
      await load();
    } finally {
      setCompiling(false);
    }
  }

  async function handleCopyLink(item: ProspectArtifactListItem) {
    const link = shareLink(item.token);
    try {
      await navigator.clipboard?.writeText(link);
    } catch {
      // Clipboard can be unavailable (no permission / insecure context).
      // The link is still visible in the row title, so fail quietly.
    }
    setCopiedId(item.id);
    window.setTimeout(() => {
      setCopiedId((prev) => (prev === item.id ? null : prev));
    }, 1500);
  }

  function handlePreview(item: ProspectArtifactListItem) {
    window.open(`/api/admin/prospects/${item.id}/preview`, "_blank", "noopener");
  }

  async function handleRevoke(item: ProspectArtifactListItem) {
    const confirmed = window.confirm(
      `Revoke the artifact for ${item.companyName ?? item.companyDomain}?\n\n` +
        "The public /share/team link will 404 immediately. This cannot be undone.",
    );
    if (!confirmed) return;
    setRevokingId(item.id);
    try {
      const res = await fetch(`/api/admin/prospects/${item.id}/revoke`, {
        method: "POST",
      });
      if (!res.ok) return;
      const row = (await res.json()) as ProspectArtifactListItem;
      setArtifacts((prev) =>
        prev.map((a) =>
          a.id === item.id
            ? { ...a, status: row.status, revokedAt: row.revokedAt }
            : a,
        ),
      );
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* ── Compile form ─────────────────────────────────────── */}
      <div
        className="rounded-[var(--radius-card)] p-4 flex flex-col gap-3"
        style={{ background: "var(--print-bg)", border: "1px solid var(--print-border)", boxShadow: "var(--shadow-print)" }}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: "var(--accent)" }} />
          <h2
            className="text-sm font-semibold"
            style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}
          >
            Compile a prospect Team
          </h2>
        </div>
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Paste a job posting or call notes plus the company domain. The compiler
          produces a shareable artifact page you send instead of a meeting ask.
        </div>

        <Textarea
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          placeholder="Paste the job posting or call notes…"
          rows={6}
          className="text-[13px] resize-y"
          aria-label="Source text"
        />

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>
              Company domain
            </label>
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="acme.com"
              inputSize="sm"
              aria-label="Company domain"
              className="w-48"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>
              Role hint (optional)
            </label>
            <Input
              value={roleHint}
              onChange={(e) => setRoleHint(e.target.value)}
              placeholder="Customer Success Lead"
              inputSize="sm"
              aria-label="Role hint"
              className="w-56"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>
              Source kind
            </label>
            <select
              value={sourceKind}
              onChange={(e) => setSourceKind(e.target.value as ProspectSourceKind)}
              aria-label="Source kind"
              className="h-7 rounded-md border px-2 text-sm"
              style={{
                borderColor: "var(--border)",
                background: "var(--bg-input)",
                color: "var(--text-primary)",
              }}
            >
              <option value="job_posting">Job posting</option>
              <option value="notes">Notes</option>
            </select>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleCompile()}
            loading={compiling}
            disabled={compiling}
          >
            <Sparkles size={13} /> Compile
          </Button>
        </div>

        {compileError && (
          <div className="text-xs" style={{ color: "var(--error)" }}>
            {compileError}
          </div>
        )}
      </div>

      {/* ── Sort toggle ──────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>
          Sort:
        </span>
        <div
          className="inline-flex rounded-md border p-0.5"
          style={{ borderColor: "var(--border)", background: "rgba(var(--glass-rgb), 0.55)" }}
          role="tablist"
          aria-label="Sort artifacts"
        >
          {(
            [
              { value: "recent", label: "Recent" },
              { value: "engaged", label: "Most engaged" },
            ] as const
          ).map((opt) => {
            const active = opt.value === sort;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSort(opt.value)}
                className="px-3 py-1 text-[12px] font-medium rounded-[4px] transition-colors"
                style={{
                  background: active ? "var(--accent-muted)" : "transparent",
                  color: active ? "var(--accent)" : "var(--text-secondary)",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => void load()}
          className="ml-auto"
        >
          <RefreshCw size={11} /> Refresh
        </Button>
      </div>

      {/* ── Artifacts table ──────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
        </div>
      ) : artifacts.length === 0 ? (
        <div
          className="rounded-[var(--radius-card)] px-4 py-8 text-center text-sm border-[1.5px] border-dashed"
          style={{ borderColor: "var(--dash-border)", color: "var(--text-secondary)" }}
        >
          No artifacts yet. Compile one above.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border" style={{ borderColor: "var(--print-border)", background: "var(--print-bg)", boxShadow: "var(--shadow-print)" }}>
          <table className="w-full text-[13px]" style={{ color: "var(--text-primary)" }}>
            <thead>
              <tr>
                <Th>Company</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Opens</Th>
                <Th>Last opened</Th>
                <Th>Claimed</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {artifacts.map((a) => (
                <tr key={a.id}>
                  <Td>{a.companyName ?? a.companyDomain}</Td>
                  <Td>{a.roleTitle}</Td>
                  <Td>
                    <span
                      className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
                      style={{
                        color: statusColor(a.status),
                        background: `color-mix(in srgb, ${statusColor(a.status)} 14%, transparent)`,
                      }}
                    >
                      {a.status}
                    </span>
                  </Td>
                  <Td>{fmtDate(a.createdAt)}</Td>
                  <Td>{a.openCount}</Td>
                  <Td>{timeAgo(a.lastOpenedAt)}</Td>
                  <Td>
                    {a.claimedAt ? (
                      <span
                        className="inline-block px-2 py-0.5 rounded text-[11px] font-medium cursor-default"
                        style={{
                          color: "var(--accent)",
                          background: "var(--accent-muted)",
                        }}
                        title={a.claimantEmail ?? undefined}
                      >
                        Claimed{a.claimantEmail ? ` · ${a.claimantEmail}` : ""}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-tertiary)" }}>—</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => void handleCopyLink(a)}
                        title={shareLink(a.token)}
                        aria-label="Copy public link"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border-none cursor-pointer"
                        style={{ background: "rgba(var(--ink-rgb), 0.06)", color: "var(--text-secondary)" }}
                      >
                        {copiedId === a.id ? (
                          <Check size={11} style={{ color: "var(--success)" }} />
                        ) : (
                          <Copy size={11} />
                        )}
                        {copiedId === a.id ? "Copied" : "Copy link"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePreview(a)}
                        aria-label="Preview"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border-none cursor-pointer"
                        style={{ background: "rgba(var(--ink-rgb), 0.06)", color: "var(--text-secondary)" }}
                      >
                        <ExternalLink size={11} /> Preview
                      </button>
                      {a.status !== "revoked" && (
                        <button
                          type="button"
                          onClick={() => void handleRevoke(a)}
                          disabled={revokingId === a.id}
                          aria-label="Revoke"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50"
                          style={{ background: "var(--error-muted)", color: "var(--error)" }}
                        >
                          {revokingId === a.id ? (
                            <Loader2 size={11} className="animate-spin" />
                          ) : (
                            <Ban size={11} />
                          )}
                          Revoke
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-medium border-b"
      style={{ color: "var(--text-label)", borderColor: "var(--border-subtle)", background: "rgba(var(--ink-rgb), 0.04)", fontFamily: "var(--font-mono)" }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-3 py-2 border-b" style={{ borderColor: "var(--border-subtle)" }}>
      {children}
    </td>
  );
}
